import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2, MapPin, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/button';
import { formatOrderNumber, formatOrderNumberCompact } from '@/shared/lib/orderDisplay';
import { formatMoney } from '../utils/formatters';
import { ordersKeys, useAcceptOrder, useRejectOrder } from '../hooks/useOrders';
import { OrderActionError, ordersApi } from '../api';
import { OrderTypePill } from './OrderTypePill';
import { PaymentTypeChip } from './PaymentTypeChip';
import { RejectOrderDialog } from './RejectOrderDialog';
import { PaymentCaptureFailedPanel, MAX_CAPTURE_RETRY_ATTEMPTS } from './PaymentCaptureFailedPanel';
import { useOrderAlertStore } from '../store/orderAlertStore';
import { ringUntilAcknowledged } from '@/features/notifications/utils/sound';

export function OrderAlertDialog() {
  const queue = useOrderAlertStore((s) => s.queue);
  const dequeue = useOrderAlertStore((s) => s.dequeue);
  const dismiss = useOrderAlertStore((s) => s.dismiss);

  const current = queue[0] ?? null;
  const queueLength = queue.length;

  // Flash tab title while an alert is pending.
  useEffect(() => {
    if (!current) return;
    const base = document.title;
    let flash = false;
    const id = setInterval(() => {
      document.title = flash ? base : `🔔 New Order! — ${base}`;
      flash = !flash;
    }, 1000);
    return () => {
      clearInterval(id);
      document.title = base;
    };
  }, [current?.orderId]);


  return (
    <AnimatePresence>
      {current && (
        <AlertOverlay
          key={current.orderId}
          orderId={current.orderId}
          restaurantId={current.restaurantId}
          queueLength={queueLength}
          onDismiss={() => dismiss(current.orderId)}
          onNext={dequeue}
        />
      )}
    </AnimatePresence>
  );
}

interface OverlayProps {
  orderId: string;
  restaurantId: string;
  queueLength: number;
  onDismiss: () => void;
  onNext: () => void;
}

function AlertOverlay({ orderId, restaurantId, queueLength, onDismiss, onNext }: OverlayProps) {
  const { t } = useTranslation();
  const accept = useAcceptOrder(restaurantId);
  const reject = useRejectOrder(restaurantId);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [captureFailure, setCaptureFailure] = useState<{ message: string | null; attempts: number } | null>(null);
  const stopRingRef = useRef<(() => void) | null>(null);

  // Start ringing immediately and stop the moment the owner acts.
  useEffect(() => {
    const stop = ringUntilAcknowledged();
    stopRingRef.current = stop;
    return stop;
  }, [orderId]);

  const silenceAndNext = useCallback(() => {
    stopRingRef.current?.();
    onNext();
  }, [onNext]);

  const { data: order } = useQuery({
    queryKey: ordersKeys.detail(restaurantId, orderId),
    queryFn: () => ordersApi.getOrder(restaurantId, orderId),
    staleTime: 30_000,
  });

  const runAccept = useCallback(() => {
    stopRingRef.current?.();
    accept.mutate(orderId, {
      onSuccess: silenceAndNext,
      onError: (err) => {
        if (err instanceof OrderActionError && err.code === 'payment_capture_failed') {
          setCaptureFailure((prev) => ({
            message: err.message ?? prev?.message ?? null,
            attempts: (prev?.attempts ?? 0) + 1,
          }));
        }
      },
    });
  }, [accept, orderId, onNext]);

  const runReject = useCallback(
    (reason: string | undefined) => {
      stopRingRef.current?.();
      reject.mutate(
        { orderId, reason },
        { onSuccess: () => { setRejectOpen(false); silenceAndNext(); } }
      );
    },
    [reject, orderId, silenceAndNext]
  );

  const retriesExhausted = (captureFailure?.attempts ?? 0) >= MAX_CAPTURE_RETRY_ATTEMPTS;
  const acceptDisabled = accept.isPending || reject.isPending || retriesExhausted;
  const rejectDisabled = reject.isPending;

  const itemsAll = order?.orderItems ?? [];
  const itemCount = itemsAll.reduce((acc, i) => acc + i.quantity, 0);
  const orderLabel = order ? formatOrderNumber(order) : '…';
  const badgeLabel = order ? formatOrderNumberCompact(order) : '…';

  const deliveryLabel = (() => {
    if (!order || order.orderType !== 'DELIVERY') return null;
    const addr = order.deliveryAddress;
    if (!addr) return null;
    const parts = [addr.street, addr.streetNumber ?? addr.houseNumber, addr.city].filter(Boolean);
    const label = parts.join(' ');
    return order.deliveryDistanceKm != null ? `${label} · ${order.deliveryDistanceKm.toFixed(1)} km` : label || null;
  })();

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Card — slides up on mobile, scales in on desktop */}
      <motion.div
        className="fixed inset-0 z-[201] flex items-end sm:items-center justify-center p-0 sm:p-4"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 60 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
      >
        <div className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
          {/* Pulsing top bar */}
          <div className="h-1.5 w-full bg-destructive animate-pulse" />

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-destructive" />
              </span>
              <span className="font-bold text-sm uppercase tracking-widest text-destructive">
                {t('orders.alert.newOrder', 'New Order')}
              </span>
            </div>
            {queueLength > 1 && (
              <span className="text-xs font-semibold bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                +{queueLength - 1} {t('orders.alert.more', 'more')}
              </span>
            )}
          </div>

          {/* Order body */}
          <div className="px-5 py-4 space-y-3">
            {/* Order ID + customer + type */}
            <div className="flex items-start gap-3">
              <div className="px-2 min-w-[44px] h-11 rounded-xl flex items-center justify-center font-bold text-sm bg-destructive/10 text-destructive shrink-0 font-mono">
                {badgeLabel}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-base leading-tight truncate">
                  {order?.customerInfo?.name || t('orders.guest', 'Guest')}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {order && <OrderTypePill type={order.orderType} />}
                  <span className="text-xs text-muted-foreground">{orderLabel}</span>
                </div>
              </div>
            </div>

            {/* Items */}
            {itemsAll.length > 0 && (
              <div className="space-y-1">
                {itemsAll.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm gap-2">
                    <span className="text-muted-foreground min-w-0 truncate">
                      {item.quantity}× {item.menuItemName ?? item.menuItemId}
                    </span>
                    {item.unitPrice != null && (
                      <span className="text-muted-foreground shrink-0">
                        {formatMoney(item.unitPrice * item.quantity)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            {order?.notes && (
              <p className="text-xs text-muted-foreground bg-muted/60 rounded-lg px-3 py-2 italic">
                "{order.notes}"
              </p>
            )}

            {/* Delivery address */}
            {deliveryLabel && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="w-3.5 h-3.5 shrink-0 text-destructive" />
                <span className="truncate">{deliveryLabel}</span>
              </p>
            )}

            {/* Total + payment */}
            <div className="flex items-center gap-3 pt-1 border-t border-border/50">
              <span className="font-bold text-lg tabular-nums">
                {order ? formatMoney(order.total) : '…'}
              </span>
              <span className="text-xs text-muted-foreground">
                {itemCount} {t('orders.items', 'items')}
              </span>
              {order?.paymentType && (
                <div className="ml-auto">
                  <PaymentTypeChip type={order.paymentType} status={order.paymentStatus} />
                </div>
              )}
            </div>

            {captureFailure && (
              <PaymentCaptureFailedPanel
                message={captureFailure.message}
                attempts={captureFailure.attempts}
                retrying={accept.isPending}
                onRetry={runAccept}
                onReject={() => setRejectOpen(true)}
              />
            )}
          </div>

          {/* Actions — pb accounts for iPhone home indicator */}
          <div className="px-5 flex gap-3" style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}>
            <Button
              size="lg"
              className="flex-1 gap-2 text-base font-semibold"
              onClick={runAccept}
              disabled={acceptDisabled}
            >
              {accept.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {accept.isPending
                ? t('orders.accepting', 'Accepting…')
                : t('orders.accept', 'Accept')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
              onClick={() => setRejectOpen(true)}
              disabled={rejectDisabled}
            >
              <X className="w-4 h-4" />
              {t('orders.reject', 'Reject')}
            </Button>
          </div>
        </div>
      </motion.div>

      <RejectOrderDialog
        open={rejectOpen}
        orderIdentifier={orderLabel}
        submitting={reject.isPending}
        onConfirm={runReject}
        onOpenChange={setRejectOpen}
      />
    </>
  );
}
